import React, { useState } from "react";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Switch from "@mui/material/Switch";
import Typography from "@mui/material/Typography";
import Uploadphotoicon from "../../Assests/Images/uploadphotoicon.png";
import { MediaPreviewDialog, isSafeMediaSrc } from "../Shared/MediaPreview";
import AdminIcon from "./adminIcons";
import { ADMIN_COLORS, ADMIN_FONT, ADMIN_INTER_FONT, ADMIN_SWITCH_SX } from "./adminUi";

/** Building blocks shared by the candidate and HR "View" drawers (Figma Employee Record). */

/** Employee Record type styles: Figma fonts, sized and weighted up for readability. */
export var RECORD_TYPE = {
  title: { fontFamily: ADMIN_FONT, fontSize: 22, fontWeight: 700, lineHeight: "32px" },
  description: { fontFamily: ADMIN_FONT, fontSize: 15, fontWeight: 400, lineHeight: "20px" },
  name: { fontFamily: ADMIN_INTER_FONT, fontSize: 19, fontWeight: 900, lineHeight: "26px" },
  meta: { fontFamily: ADMIN_INTER_FONT, fontSize: 14, fontWeight: 600, lineHeight: "20px" },
  sectionTitle: { fontFamily: ADMIN_INTER_FONT, fontSize: 18, fontWeight: 900, lineHeight: "26px" },
  label: { fontFamily: ADMIN_INTER_FONT, fontSize: 14, fontWeight: 600, lineHeight: "20px" },
  value: { fontFamily: ADMIN_FONT, fontSize: 16, fontWeight: 600, lineHeight: "22px" },
  item: { fontFamily: ADMIN_FONT, fontSize: 15, fontWeight: 500, lineHeight: "20px" },
};

/** Record status pill (Figma 124 × 22, enlarged to match RECORD_TYPE). */
export var RECORD_CHIP_SX = { width: 160, height: 26, flexShrink: 0, fontSize: 13, fontWeight: 600 };

var RECORD_AVATAR_SIZE = 68;

/** Bold section heading with a divider below; `action` sits on the right, `onToggle` makes it collapsible. */
export function RecordSectionHeader(props) {
  var collapsible = Boolean(props.onToggle);
  var title = (
    <Typography component="h3" sx={Object.assign({ color: ADMIN_COLORS.label }, RECORD_TYPE.sectionTitle)}>
      {props.title}
    </Typography>
  );
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "16px",
        minHeight: 32,
        pb: "8px",
        borderBottom: "1px solid " + ADMIN_COLORS.lineSoft,
      }}
    >
      {collapsible ? (
        <ButtonBase
          onClick={props.onToggle}
          aria-expanded={Boolean(props.expanded)}
          sx={{ flex: 1, justifyContent: "space-between", borderRadius: "4px", textAlign: "left" }}
        >
          {title}
          <AdminIcon
            name="CaretDown"
            size={20}
            sx={{
              color: ADMIN_COLORS.labelMuted,
              transform: props.expanded ? "rotate(180deg)" : "none",
              transition: "transform 150ms",
            }}
          />
        </ButtonBase>
      ) : (
        title
      )}
      {props.action || null}
    </Box>
  );
}

var AVATAR_IMG_SX = {
  display: "block",
  width: RECORD_AVATAR_SIZE,
  height: RECORD_AVATAR_SIZE,
  borderRadius: "6px",
  objectFit: "cover",
  bgcolor: ADMIN_COLORS.page,
};

/** Figma content switcher: #F1F2F4 track, white tabs, #D3E6FF for the selected one. */
export function RecordTabs(props) {
  function handleKeyDown(event) {
    var index = props.tabs.findIndex(function (tab) {
      return tab.id === props.value;
    });
    var step = event.key === "ArrowRight" ? 1 : event.key === "ArrowLeft" ? -1 : 0;
    if (!step) return;
    event.preventDefault();
    var next = props.tabs[(index + step + props.tabs.length) % props.tabs.length];
    props.onChange(next.id);
  }

  return (
    <Box
      role="tablist"
      aria-label={props.ariaLabel}
      onKeyDown={handleKeyDown}
      sx={{ display: "flex", gap: "6px", p: "4px", borderRadius: "8px", bgcolor: "#F1F2F4" }}
    >
      {props.tabs.map(function (tab) {
        var active = tab.id === props.value;
        return (
          <ButtonBase
            key={tab.id}
            role="tab"
            id={"record-tab-" + tab.id}
            aria-selected={active}
            aria-controls={"record-tabpanel-" + tab.id}
            tabIndex={active ? 0 : -1}
            onClick={function () {
              props.onChange(tab.id);
            }}
            sx={{
              flex: "1 0 0",
              minHeight: 34,
              px: "12px",
              borderRadius: "4px",
              bgcolor: active ? ADMIN_COLORS.tabActive : ADMIN_COLORS.card,
              color: ADMIN_COLORS.label,
              fontFamily: ADMIN_FONT,
              fontSize: 15,
              fontWeight: active ? 600 : 500,
              lineHeight: "20px",
              "&:hover": { bgcolor: active ? ADMIN_COLORS.tabActive : ADMIN_COLORS.brandSoft },
              "&.Mui-focusVisible": { outline: "2px solid " + ADMIN_COLORS.brand, outlineOffset: "-2px" },
            }}
          >
            {tab.label}
          </ButtonBase>
        );
      })}
    </Box>
  );
}

/** Photo (click to enlarge / download), name with the status chip on the right, subtitle line and email link. */
export function RecordProfileHeader(props) {
  var [photoOpen, setPhotoOpen] = useState(false);
  var hasPhoto = isSafeMediaSrc(props.photo);
  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: "12px", minWidth: 0 }}>
      {hasPhoto ? (
        <ButtonBase
          aria-label="View photo"
          title="Click to view"
          onClick={function () {
            setPhotoOpen(true);
          }}
          sx={{
            flexShrink: 0,
            borderRadius: "6px",
            cursor: "zoom-in",
            "&.Mui-focusVisible": { outline: "2px solid " + ADMIN_COLORS.brand, outlineOffset: "2px" },
          }}
        >
          <Box component="img" src={props.photo} alt="" sx={AVATAR_IMG_SX} />
        </ButtonBase>
      ) : (
        <Box component="img" src={Uploadphotoicon} alt="" sx={Object.assign({ flexShrink: 0 }, AVATAR_IMG_SX)} />
      )}
      {hasPhoto ? (
        <MediaPreviewDialog
          open={photoOpen}
          onClose={function () {
            setPhotoOpen(false);
          }}
          src={props.photo}
          fallbackName={(props.name ? props.name + " " : "") + "photo"}
          title="Photo"
        />
      ) : null}
      <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "4px" }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", minWidth: 0 }}>
          <Typography
            noWrap
            title={props.name || ""}
            sx={Object.assign({ color: ADMIN_COLORS.label, minWidth: 0 }, RECORD_TYPE.name)}
          >
            {props.name || "—"}
          </Typography>
          {props.chip || null}
        </Box>
        <Typography noWrap sx={Object.assign({ color: ADMIN_COLORS.labelSecondary }, RECORD_TYPE.meta)}>
          {props.subtitle || "—"}
        </Typography>
        {props.email ? (
          <Typography
            noWrap
            component="a"
            href={"mailto:" + props.email}
            title={props.email}
            sx={Object.assign(
              { color: ADMIN_COLORS.link, textDecoration: "none", "&:hover": { textDecoration: "underline" } },
              RECORD_TYPE.meta
            )}
          >
            {props.email}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
}

/** Label above a value; pass children to render a custom control instead of text. */
export function RecordInfoItem(props) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "6px", py: "10px", minWidth: 0 }}>
      <Typography sx={Object.assign({ color: ADMIN_COLORS.labelSecondary }, RECORD_TYPE.label)}>{props.label}</Typography>
      {props.children || (
        <Typography sx={Object.assign({ color: ADMIN_COLORS.label, wordBreak: "break-word" }, RECORD_TYPE.value)}>
          {props.value || "—"}
        </Typography>
      )}
    </Box>
  );
}

/** Two columns from tablet up, one on narrow phones. */
export var RECORD_INFO_GRID_SX = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
  columnGap: "16px",
  pt: "6px",
};

/** Login switch, used inside RecordInfoItem. */
export function RecordLoginSwitch(props) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", minHeight: 24 }}>
      <Switch
        checked={Boolean(props.checked)}
        disabled={!props.onChange}
        onChange={function (e, checked) {
          props.onChange(checked);
        }}
        inputProps={{ "aria-label": props.ariaLabel || "Toggle login" }}
        sx={ADMIN_SWITCH_SX}
      />
    </Box>
  );
}
